import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, MagnifyingGlass, Star, Trophy, UsersThree } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { API, mediaUrl } from "../lib/api";
import { usePublicLocation } from "../hooks/usePublicCity";

function TeamLeaderboard({ leaderboard, error }) {
  const monthLabel = leaderboard?.month
    ? new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" })
      .format(new Date(`${leaderboard.month}-01T00:00:00Z`)).toUpperCase()
    : "THIS MONTH";
  const teams = leaderboard?.teams || [];

  return (
    <section data-testid="team-leaderboard" className="mt-12">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="neon-overline !text-neon-gold">PROMO TEAMS · {monthLabel}</div>
          <h2 className="mt-2 font-display text-3xl font-black tracking-tight">TOP TEAMS THIS MONTH</h2>
          <p className="mt-2 max-w-xl text-sm text-zinc-500">
            Ranked by unique guests scanned at team events. Re-entries do not count.
          </p>
        </div>
        <div className="hidden h-14 w-14 items-center justify-center rounded-2xl border border-neon-gold/30 bg-neon-gold/10 text-neon-gold sm:flex">
          <Trophy size={26} weight="duotone" />
        </div>
      </div>

      {error ? (
        <div data-testid="team-leaderboard-error"
          className="mt-6 rounded-3xl border border-red-400/25 bg-red-400/5 p-6 text-sm text-red-200">
          The leaderboard is warming up. Refresh in a moment.
        </div>
      ) : leaderboard === null ? (
        <div data-testid="team-leaderboard-loading" className="mt-6 grid gap-3 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-3xl border border-white/10 bg-white/[0.03]" />
          ))}
        </div>
      ) : teams.length === 0 ? (
        <div data-testid="team-leaderboard-empty"
          className="mt-6 rounded-3xl border border-neon-gold/25 bg-neon-gold/5 p-8 text-center">
          <UsersThree size={32} className="mx-auto text-neon-gold" />
          <p className="mt-3 font-display text-lg font-black">No team scans yet this month</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-zinc-500">
            The first promo team to bring guests through the door takes the top spot.
          </p>
        </div>
      ) : (
        <div data-testid="team-leaderboard-list" className="mt-6 grid gap-3 lg:grid-cols-2">
          {teams.map((team, i) => (
            <motion.div key={team.team_id} data-testid={`team-leaderboard-team-${team.team_id}`}
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.35 }}
              className={`relative overflow-hidden rounded-3xl border p-4 ${
                i === 0
                  ? "border-neon-gold/50 bg-neon-gold/[0.07] shadow-glow-gold"
                  : "border-white/10 bg-[#0a0a0a]"
              }`}>
              {i === 0 && <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-neon-gold/15 blur-2xl" />}
              <div className="relative flex items-center gap-4">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl font-display text-lg font-black ${
                  i === 0 ? "bg-neon-gold text-black" : "border border-white/10 bg-white/[0.04] text-neon-gold"
                }`}>
                  {team.rank}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate font-display text-base font-black">{team.name}</div>
                    {i === 0 && (
                      <span className="rounded-full border border-neon-gold/40 bg-neon-gold/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-neon-gold">
                        Leading
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {team.members.slice(0, 4).map((member) => member.avatar_url ? (
                        <img key={member.id} src={mediaUrl(member.avatar_url)} alt={member.name}
                          className="h-7 w-7 rounded-full border-2 border-[#050505] object-cover" />
                      ) : (
                        <div key={member.id}
                          className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#050505] bg-neon-violet/20 text-[10px] font-black text-neon-violet">
                          {(member.name || "?")[0].toUpperCase()}
                        </div>
                      ))}
                      {team.members.length > 4 && (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#050505] bg-white/10 text-[9px] font-black text-zinc-300">
                          +{team.members.length - 4}
                        </div>
                      )}
                    </div>
                    <span className="text-[11px] font-semibold text-zinc-500">
                      {team.members.length} member{team.members.length === 1 ? "" : "s"} · {team.events} event{team.events === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div data-testid={`team-leaderboard-guests-${team.team_id}`}
                    className="font-display text-3xl font-black text-neon-gold">{team.unique_guests}</div>
                  <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500">Unique guests</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function PromoterDirectory() {
  const [promoters, setPromoters] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardError, setLeaderboardError] = useState(false);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [showAll, setShowAll] = useState(false);
  const location = usePublicLocation();
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/promoters/team-leaderboard")
      .then((r) => setLeaderboard(r.data))
      .catch(() => setLeaderboardError(true));
  }, []);

  useEffect(() => {
    if (!location.resolved) return;
    const params = {};
    if (!showAll && location.has_location) {
      params.city = location.city;
      if (location.lat !== null && location.lng !== null) {
        params.lat = location.lat;
        params.lng = location.lng;
        params.max_km = 50;
      }
    }
    API.get("/promoters", { params }).then((r) => setPromoters(r.data)).catch(() => setPromoters([]));
  }, [location.resolved, location.has_location, location.city, location.lat, location.lng, showAll]);

  const filtered = (promoters || []).filter((p) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return p.name.toLowerCase().includes(needle) || p.handle.toLowerCase().includes(needle.replace(/^@/, ""));
  });

  return (
    <div data-testid="promoter-directory" className="min-h-screen bg-[#050505] text-white">
      <header className="glass sticky top-0 z-40" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="promoters-logo" className="flex items-center gap-3">
            <ArrowLeft size={16} className="text-zinc-500" />
            <img src="/logo.png" alt="NITEPASS" className="h-12 w-auto" />
          </Link>
          <Link to="/auth?mode=register" data-testid="promoters-get-pass"
            className="btn-neon !px-5 !py-2 text-sm">Get the Pass</Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="neon-overline !text-neon-violet" data-testid="promoters-city-label">{location.city.toUpperCase()} · ON NITEPASS</div>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-5xl">FIND A PROMOTER</h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-400">
          The people moving the city tonight. Follow their guest lists, see their scores, join their nights.
        </p>

        <div className="mt-6 flex max-w-md items-center gap-2 rounded-full border border-white/10 bg-[#0a0a0a] px-5 py-3" data-testid="promoter-search-box">
          <MagnifyingGlass size={16} className="shrink-0 text-neon-violet" />
          <input data-testid="promoter-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or @handle…"
            className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-600" />
        </div>

        {location.has_location && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="promoter-location-scope">
            <button data-testid="promoters-nearby-toggle" onClick={() => setShowAll(false)}
              className={`rounded-full border px-4 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ${!showAll ? "border-neon-violet/60 bg-neon-violet/15 text-neon-violet" : "border-white/10 text-zinc-500 hover:text-white"}`}>
              Near {location.city}
            </button>
            <button data-testid="promoters-all-cities-toggle" onClick={() => setShowAll(true)}
              className={`rounded-full border px-4 py-1.5 text-[11px] font-black uppercase tracking-wider transition-colors duration-200 ${showAll ? "border-neon-violet/60 bg-neon-violet/15 text-neon-violet" : "border-white/10 text-zinc-500 hover:text-white"}`}>
              All Cities
            </button>
          </div>
        )}

        <TeamLeaderboard leaderboard={leaderboard} error={leaderboardError} />

        {promoters === null ? (
          <p className="py-20 text-center text-sm text-zinc-600">Loading promoters…</p>
        ) : filtered.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-neon-violet/25 bg-neon-violet/5 p-10 text-center" data-testid="promoters-empty">
            <UsersThree size={36} className="mx-auto text-neon-violet" />
            <p className="mt-4 font-display text-xl font-black">
              {promoters.length === 0 && location.has_location && !showAll
                ? `No promoters near ${location.city} yet`
                : promoters.length === 0
                  ? "The first promoters are joining now"
                  : `No promoters match "${q}"`}
            </p>
            {promoters.length === 0 && location.has_location && !showAll ? (
              <>
                <p className="mx-auto mt-2 max-w-sm text-sm text-zinc-500">
                  The night is still building here. Browse every NITEPASS promoter instead.
                </p>
                <button data-testid="promoters-show-all-empty" onClick={() => setShowAll(true)}
                  className="btn-neon mt-6 inline-flex !px-6 !py-2.5 text-sm">View All Cities</button>
              </>
            ) : promoters.length === 0 && (
              <>
                <p className="mx-auto mt-2 max-w-sm text-sm text-zinc-500">
                  Run the night? Claim your @handle before someone else does.
                </p>
                <Link to="/auth?mode=register" data-testid="promoters-empty-cta"
                  className="btn-neon mt-6 inline-flex !px-6 !py-2.5 text-sm">Become a Promoter</Link>
              </>
            )}
          </div>
        ) : (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="promoters-grid">
            {filtered.map((p, i) => (
              <motion.button key={p.id} data-testid={`promoter-card-${p.handle}`}
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                onClick={() => navigate(`/${p.handle}`)}
                className="group flex items-center gap-4 rounded-3xl border border-white/10 bg-[#0a0a0a] p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:border-neon-violet/50 hover:shadow-glow-violet">
                {p.avatar_url ? (
                  <img src={mediaUrl(p.avatar_url)} alt={p.name}
                    className="h-16 w-16 rounded-full border-2 border-neon-violet/40 object-cover" />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 border-neon-gold/40 bg-neon-gold/10 font-display text-2xl font-black text-neon-gold">
                    {p.name[0].toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate font-display text-base font-black">{p.name}</div>
                  <div className="truncate text-xs font-bold text-neon-violet">{p.handle}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] font-bold">
                    <span className="rounded-full border border-neon-gold/40 bg-neon-gold/10 px-2 py-0.5 uppercase tracking-wider text-neon-gold">{p.title || "Party Promoter"}</span>
                    <span className="flex items-center gap-1 text-zinc-500"><Star size={10} className="text-neon-gold" /> {p.score ?? 50}</span>
                    <span className="text-zinc-600">{p.events_hosted || 0} events</span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
